import { RefObject } from 'react'
import { useElementHeight } from 'hooks/useElementSize'

interface UseMatchHeightToElementOptions {
  /**
   * Minimum height to return (prevents content from collapsing too small)
   */
  minHeight?: number
  /**
   * Fallback height to use before measurement is available (e.g., on SSR or first render)
   */
  fallbackHeight: number
  /**
   * Debounce time in ms for height measurements.
   * @default 100
   */
  debounceTime?: number
}

/**
 * Hook to measure a target element's height and return a value suitable for
 * matching another element's height to it.
 *
 * @param targetRef - Ref to the element whose height should be measured
 * @param options - Configuration options
 * @returns The calculated height, or fallbackHeight if not yet measured
 */
function useMatchHeightToElement(
  targetRef: RefObject<HTMLElement | null>,
  options: UseMatchHeightToElementOptions = { fallbackHeight: 208 },
): number {
  const {
    minHeight = 0,
    fallbackHeight,
    debounceTime = 100,
  } = options

  const measuredHeight = useElementHeight(targetRef, { debounceTime })

  if (measuredHeight === null) {
    return fallbackHeight
  }

  return Math.max(minHeight, measuredHeight)
}

export default useMatchHeightToElement
